import { useEffect, useState } from 'react';
import { Box, Alert, Typography, Chip, Card, CardContent, Grid } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import { settingsService } from '../services/settingsService';
import { useContextStore } from '../store/contextStore';
import type { TenantSettingsDto } from '../types/settings';

/**
 * Tenant settings — read-only view.
 */
export const TenantSettingsPage = () => {
  const { currentTenant } = useContextStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TenantSettingsDto | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!currentTenant) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const settings = await settingsService.getTenantSettings(currentTenant.id);
        if (!cancelled) {
          setData(settings);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load tenant settings');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentTenant?.id]);

  if (!currentTenant) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="info">Please select a tenant first to view tenant settings.</Alert>
      </Box>
    );
  }

  const subtitle = [
    `Tenant: ${currentTenant.name}`,
    ...(data?.tenantId ? [data.tenantId] : []),
  ].join(' \u2022 ');

  const formatBool = (value: boolean) => (value ? 'Yes' : 'No');
  const renderValue = (value: string | null | undefined, fallback = 'Not set') => value ?? fallback;

  return (
    <PageDetailLayout
      title="Tenant Settings"
      subtitle={subtitle}
      icon={<BusinessIcon sx={{ color: 'var(--mui-palette-primary-main)' }} />}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
    >
      {data && (
        <>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em', mb: 3, display: 'block' }}>
                Branding
              </Typography>
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Logo URL
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {renderValue(data.logoUrl)}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Brand Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {data.brandColor && (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '4px',
                          border: '1px solid var(--glass-border)',
                          backgroundColor: data.brandColor,
                        }}
                      />
                    )}
                    <Typography variant="body1" fontFamily={data.brandColor ? 'monospace' : undefined}>
                      {renderValue(data.brandColor)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Custom Domain
                  </Typography>
                  <Typography variant="body1">{renderValue(data.customDomain)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em', mb: 3, display: 'block' }}>
                Security
              </Typography>
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Require MFA
                  </Typography>
                  <Chip
                    label={data.requireMfa ? 'Required' : 'Optional'}
                    color={data.requireMfa ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Session Timeout
                  </Typography>
                  <Typography variant="body1">{data.sessionTimeoutMinutes} minutes</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Password Minimum Length
                  </Typography>
                  <Typography variant="body1">{data.passwordMinLength} characters</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    Password Requirements
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2">Uppercase: {formatBool(data.passwordRequireUppercase)}</Typography>
                    <Typography variant="body2">Lowercase: {formatBool(data.passwordRequireLowercase)}</Typography>
                    <Typography variant="body2">Number: {formatBool(data.passwordRequireNumber)}</Typography>
                    <Typography variant="body2">Special character: {formatBool(data.passwordRequireSpecialChar)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </PageDetailLayout>
  );
};
